import React, {useEffect, useState} from 'react'; 
import {Switch, useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import { PharmacyMedications } from './PharmacyMedications';
import { QueryParams } from '../common/constants';
import {useAsync} from "../common/async";
import {createEncounter, EncounterDetail} from "../resident-medications/createEncounter";
import {LoadingPlaceholder} from "@communicare/medications/Components/Placeholder";
import { logger } from '../common/logger';
import {usePharmacyRequestAddedSignalR} from '../signalR/subscriptions';
import { getResidentMedicationDetailAndResetDraft } from './residentMedicationDetails';
import { patientService } from '../../services/medicationmanagement/patientApi/PatientApi';
import { Patient } from '../../services/medicationmanagement/patientApi/models/Patient';
import { ComponentLoader, NonMandatory } from '../common';
import { useReducer } from 'react';
import { PatientAllergyInfo } from '../../services/medicationmanagement/patientApi/models/PatientAllergyInfo';
import { getChartContact, getChartSummary } from '../../services/medicationmanagement/medications/MedicationsApi';
import { IndividualHealthcareIdentifier } from '../../services/healthcareidentifier/models/IndividualHealthcareIdentifier';
import { PharmacyRoute } from '../common/route-extensions/PharmacyRoute';
import { ConfirmMedicationChange } from './confirm-medication-change/ConfirmMedicationChange';
import Footer from '../common/footer/Footer';


interface ResidentDetailState{
    data:{
        resident:NonMandatory<Patient>;
        allergyInfo:NonMandatory<PatientAllergyInfo>;
        ihi:NonMandatory<IndividualHealthcareIdentifier>;
    };
    progress:Action;
}

type Action = 'Start_Loading_Details'|'Details_Loaded' | 'Details_Loading_Failed' | 'Start_Loading_ChartInfo' | 'ChartInfo_Loaded' | 'Start_Loading_ContactInfo' | 'ContactInfo_Loaded';

const residentDetailsReducer = (state:ResidentDetailState,action:[Action,any]):ResidentDetailState=>{
    let type = action[0];
    let payload = action[1];
    switch (type) {
        case 'Start_Loading_Details':
            return {
                ...state,
                progress:'Start_Loading_Details'
            };
        case 'Details_Loaded':
            return {
                ...state,
                data: payload,
                progress:'Details_Loaded'
            };
        case 'Details_Loading_Failed':
            return {
                ...state,
                data: {
                    allergyInfo:null,
                    resident:null,
                    ihi:null
                },
                progress:'Details_Loading_Failed'
            };
        case 'ChartInfo_Loaded':
            return {
                ...state,
                data:{
                    ...state.data,
                    resident:state.data?.resident?{
                        ...state.data.resident,
                        chart:payload
                    }:null
                },
                progress:'ChartInfo_Loaded'
            };
        case 'ContactInfo_Loaded':
            return {
                ...state,
                data:{
                    ...state.data,
                    resident:state.data?.resident?{
                        ...state.data.resident,
                        contacts:payload
                    }:null
                },
                progress:'ContactInfo_Loaded'
            };
    }
    return state;
}

const useResidentDetails = (authoringSystem:string,authoringSystemForeignKey:string)=>{

    let [state,dispatch] = useReducer(residentDetailsReducer,{
        progress:'Start_Loading_Details'
    } as ResidentDetailState);

    const location = useLocation()
    useEffect(() => {
        dispatch(['Start_Loading_Details',null]);
        Promise.all([
            getResidentMedicationDetailAndResetDraft(authoringSystem, authoringSystemForeignKey),
            patientService.getPatientAllergyInfo(authoringSystem,authoringSystemForeignKey),
            getChartSummary(authoringSystem, authoringSystemForeignKey),
            patientService.getIndividualHealthcareIdentifier(authoringSystem, authoringSystemForeignKey),
            getChartContact(authoringSystem, authoringSystemForeignKey)
        ]).then(x=>{
            dispatch(['Details_Loaded',{
                resident:{
                    ...x[0],
                    chart:x[2],
                    contacts:x[4]
                },
                allergyInfo:x[1],
                ihi:x[3]
            }]);
        }).catch(_=>{
            dispatch(['Details_Loading_Failed',null]);
        });

    }, [authoringSystem, authoringSystemForeignKey, location]);

    return {
        ...state,
        loadChartDetails:()=>{
            dispatch(['Start_Loading_ChartInfo',null]);
            getChartSummary(authoringSystem,authoringSystemForeignKey)
            .then(x=>{
                dispatch(['ChartInfo_Loaded',x]);
            });
        },
        loadContactDetails:()=>{
            dispatch(['Start_Loading_ContactInfo',null]);
            getChartContact(authoringSystem,authoringSystemForeignKey)
            .then(x=>{
                dispatch(['ContactInfo_Loaded',x]);
            });
        }
    }
};

export const PharmacyChart = (props:any)=>{

    let { path,url } = useRouteMatch();
    let params: any = useParams();
    let authoringSystem = params[QueryParams.AuthoringSystem];
    let authoringSystemForeignKey = params[QueryParams.AuthoringSystemForeignKey];    
    let { inProgress, success,error, queryDispatcher } = useAsync();

    useEffect(() => {
        queryDispatcher(createEncounter(authoringSystem, authoringSystemForeignKey));
    }, [authoringSystem, authoringSystemForeignKey]);

    let state = useResidentDetails(authoringSystem, authoringSystemForeignKey);

    usePharmacyRequestAddedSignalR({
        patient:{
        authoringSystem:authoringSystem||'',
        authoringSystemForeignKey:authoringSystemForeignKey||''
        },
        onSignalResponse:(data:any)=>{
            logger.log('Signal from server',data);
            state.loadChartDetails();
            state.loadContactDetails();
        }
    });

    let loadChartContactDetails = () => {
        state.loadContactDetails();
    };

    let resident = state.data?.resident;
    let allergyInfo =  state.data?.allergyInfo;
    let ihi = state.data?.ihi;
    let dataLoading = state.progress==='Start_Loading_Details';
    let dataLoadingError = state.progress==='Details_Loading_Failed'
    let {push} = useHistory()

    let chartPath = `${url}`;
    let pharmacyPath=`${chartPath}/pharmacy-request/new`;
    
    
    let navigateToPharmacyRequest = ()=>{
        push(pharmacyPath,{
            hideLoadAnimation:true,
            onCloseRouteToMedications:true
        })
    };

    let navigateBackToChart = (medicationId?: string) => {
        push(chartPath, {
            hideLoadAnimation: true,
            onCloseRouteToMedications: true
        })
    };

    let encounter: EncounterDetail = success;

    if (inProgress) {
        return <LoadingPlaceholder />
    }
    if (!success && !error){
        return null;
    }
    
    return (
     <>
        <Switch>
            <PharmacyRoute path={`${path}/confirm/:${QueryParams.PharmacyMedicationOrderId}`}>
                <ConfirmMedicationChange onClose={navigateBackToChart} />
            </PharmacyRoute>

            <PharmacyRoute path={path}>
                <ComponentLoader inProgress={dataLoading} error={dataLoadingError} emptyResultMessage="" empty={false}>
                { ihi && resident && allergyInfo? 
                        <PharmacyMedications resident={resident} allergyInfo={allergyInfo} ihi={ihi} loadChartContactDetails={loadChartContactDetails} encounter={{
                        authoringSystem:encounter.authoringSystem,
                        authoringSystemForeignKey:encounter.authoringSystemForeignKey
                        }} createPharmacyRequest={navigateToPharmacyRequest}/>:<></>
                    }
                </ComponentLoader>
            </PharmacyRoute>
        </Switch>
        <Footer lmsLink={props.lmsLink} lmsApiError={props.lmsApiError}/>
     </>
    );
};
